import classNames from 'classnames';

import { CloseIcon } from '../../../../icons';

import AddFacetSelect from '../AddFacetSelect';
import ValueSelectList from '../ValueSelectList';

import { buildFacetGroups, facetById } from '../../utilities/helpers';
import type { Facet, FilterLogic, FilterRow } from '../../utilities/types';

import styles from './_index.module.scss';

export type FilterBarProps = {
  facets: Facet[];
  logic: FilterLogic;
  onLogicChange: (logic: FilterLogic) => void;
  onRowsChange: (rows: FilterRow[]) => void;
  rows: FilterRow[];
};

/**
 * Filters builder. Each row is one facet whose values are OR-ed together; rows
 * are combined by the single AND/OR toggle (default AND, shown at 2+ rows).
 * Ports Cardify's FilterBuilder, replacing its static AND hint with a real
 * toggle and adding a per-row "clear" (unselect-all) alongside remove-row.
 */
const FilterBar = ({ facets, logic, onLogicChange, onRowsChange, rows }: FilterBarProps) => {
  const usedIds = new Set(rows.map((row) => row.facetId));
  const groups = buildFacetGroups(facets, {
    excludeIds: usedIds,
    predicate: (facet) => facet.filterable,
  });

  const addRow = (facetId: string) => {
    if (facetId) onRowsChange([...rows, { facetId, values: [] }]);
  };
  const updateRow = (index: number, values: string[]) =>
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, values } : row)));
  const removeRow = (index: number) => onRowsChange(rows.filter((_, i) => i !== index));

  return (
    <section className={styles['filter-bar']} data-testid="global-filters-bar">
      <header className={styles['header']}>
        <h4 className={styles['title']}>Filters</h4>
        {rows.length > 1 && (
          <div aria-label="Combine filters with" className={styles['logic-toggle']} role="group">
            <span className={styles['logic-label']}>Match</span>
            {(['AND', 'OR'] as const).map((option) => (
              <button
                aria-pressed={logic === option}
                className={classNames(styles['logic-option'], {
                  [styles['is-active']]: logic === option,
                })}
                key={option}
                onClick={() => onLogicChange(option)}
                type="button"
              >
                {option === 'AND' ? 'All' : 'Any'}
              </button>
            ))}
          </div>
        )}
      </header>

      {rows.length === 0 && (
        <p className={styles['empty']}>No filters — showing all respondents.</p>
      )}

      {rows.map((row, index) => {
        const facet = facetById(facets, row.facetId);
        if (!facet) return null;
        return (
          <div className={styles['row']} data-testid="global-filters-row" key={row.facetId}>
            <div className={styles['row-header']}>
              <strong className={styles['row-title']} title={facet.tooltip ?? facet.label}>
                {facet.label}
              </strong>
              <div className={styles['row-actions']}>
                {row.values.length > 0 && (
                  <button
                    className={styles['row-clear']}
                    onClick={() => updateRow(index, [])}
                    type="button"
                  >
                    Clear
                  </button>
                )}
                <button
                  aria-label={`Remove filter on ${facet.label}`}
                  className={styles['row-remove']}
                  onClick={() => removeRow(index)}
                  type="button"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            <ValueSelectList
              idPrefix={`filter-${facet.id}`}
              onChange={(values) => updateRow(index, values)}
              options={facet.options}
              searchPlaceholder={`Search ${facet.label.toLowerCase()}…`}
              selectedValues={row.values}
            />
          </div>
        );
      })}

      {groups.length > 0 ? (
        <AddFacetSelect
          aria-label="Add filter"
          data-testid="global-filters-add"
          groups={groups}
          onSelect={addRow}
          placeholder="+ Add filter…"
          value={null}
        />
      ) : (
        rows.length > 0 && <p className={styles['empty']}>All filters added.</p>
      )}
    </section>
  );
};

export default FilterBar;
